// Author: Ahsan Saleem
/**
 * BloomOS cold start. A short (~900ms) sequence built around the Bloom mark with
 * genuine states — connecting, authenticating, loading the facility, restoring
 * your workspace, finalizing. Skipped on warm navigation. If a step
 * fails, the same surface becomes a recovery screen.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { RotateCw } from 'lucide-react';
import { Bloom } from './Bloom.js';

const WARM_KEY = 'bloomos_booted';
const STEPS = ['Connecting', 'Authenticating', 'Loading facility context', 'Restoring workspace', 'Finalizing'];

export function Boot({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<'boot' | 'reveal' | 'done'>(
    sessionStorage.getItem(WARM_KEY) === '1' ? 'done' : 'boot',
  );
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string>();

  useEffect(() => {
    // Run the cold-start sequence exactly once on mount. This effect must NOT depend
    // on `phase`: it calls setPhase('reveal'/'done') itself, and a phase dependency
    // would fire this effect's cleanup mid-sequence (cancelled = true) and strand the
    // boot screen on the last step. The warm flag is the correct "already booted" gate.
    if (sessionStorage.getItem(WARM_KEY) === '1') return;
    let cancelled = false;
    const dwell = (ms: number) => new Promise((r) => setTimeout(r, ms));
    (async () => {
      try {
        setStep(0);
        const res = await Promise.race([
          fetch('/api/v1/health'),
          dwell(2500).then(() => { throw new Error('The facility server is not responding.'); }),
        ]) as Response;
        if (!res.ok && res.status >= 500) throw new Error('The facility server is unavailable.');
        await dwell(150); if (cancelled) return;
        setStep(1); await dwell(150); if (cancelled) return;
        setStep(2); await dwell(200); if (cancelled) return;
        setStep(3); await dwell(140); if (cancelled) return;
        setStep(4); await dwell(200); if (cancelled) return;
        sessionStorage.setItem(WARM_KEY, '1');
        setPhase('reveal'); await dwell(280); if (cancelled) return;
        setPhase('done');
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Startup failed.');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (phase === 'done') return <>{children}</>;

  if (error) {
    return (
      <div className="boot">
        <div className="boot-inner">
          <Bloom size={72} state="critical" />
          <div className="boot-word">BloomOS</div>
          <p className="boot-error">{error}</p>
          <button className="btn btn-primary" onClick={() => location.reload()}><RotateCw size={16} /> Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`boot${phase === 'reveal' ? ' is-reveal' : ''}`}>
      <div className="boot-inner">
        <Bloom size={72} state={step >= 4 ? 'healthy' : 'loading'} />
        <div className="boot-word">BloomOS</div>
        <div className="boot-step" aria-live="polite">{STEPS[step]}…</div>
        <div className="boot-track" aria-hidden>
          {STEPS.map((_, i) => <span key={i} className={`boot-tick${i <= step ? ' on' : ''}`} />)}
        </div>
      </div>
    </div>
  );
}
